/**
 * `createApiRelatedAddressesSource` — the `related-addresses-mgmt` port over the
 * shared `ApiClient`, plus the polling policy the address page needs.
 *
 * The typed contract itself lives in `@authe/fe-commons/related-addresses`
 * (shared with the other portals); this module binds it to the dashboard's client
 * and owns the one thing that is a *client* concern rather than a backend one:
 * keeping a polled run's status monotonic. Canonical `/related-addresses/*` paths
 * are mapped onto the service's base path by `RESOURCE_REWRITES`.
 *
 * Clustering an address walks its history across every chain it appears on, so
 * the backend records a run and enqueues it rather than answering inline. The
 * shape that follows from that: read what is already stored (cheap), and only ask
 * for a run when someone explicitly clicks.
 */
import { type ApiClient } from "@authe/fe-commons/api";
import {
  CLUSTER_RUN_STATUSES,
  createRelatedAddressesApi,
  type ClusterRun,
  type ClusterRunStatus,
  type RelatedAddressesApi,
} from "@authe/fe-commons/related-addresses";

export type RelatedAddressesSource = RelatedAddressesApi;

/** Build the port. */
export function createApiRelatedAddressesSource(client: ApiClient): RelatedAddressesSource {
  return createRelatedAddressesApi(client);
}

/**
 * Lifecycle rank — a run only moves forward: queued (0) → running (1) → terminal
 * (2). An unknown status ranks 0 so it can never freeze a run that has advanced.
 */
export function clusterRunRank(status: ClusterRunStatus | string | null | undefined): number {
  switch (status) {
    case "queued":
      return 0;
    case "running":
      return 1;
    case "succeeded":
    case "partial":
    case "failed":
      return 2;
    default:
      return 0;
  }
}

/**
 * Reconcile two views of the SAME run, keeping status monotonic.
 *
 * The run rows are read with DynamoDB's default eventual consistency, so a poll
 * issued moments after the worker's write can be served a stale copy — showing a
 * run that is already `running` as `queued` again. A lower-ranked incoming status
 * is treated as that stale read and discarded; the counters the worker only ever
 * increments likewise never regress. Without this the status line flickers
 * backwards, which reads as the run having restarted.
 */
export function mergeClusterRun(prev: ClusterRun | undefined, next: ClusterRun): ClusterRun {
  if (!prev || prev.run_id !== next.run_id) return next;
  if (clusterRunRank(next.status) < clusterRunRank(prev.status)) return prev;
  return {
    ...next,
    // Provenance and outcome fields a lagging read can blank.
    seed_cluster_id: next.seed_cluster_id ?? prev.seed_cluster_id,
    finished_at: next.finished_at ?? prev.finished_at,
    started_at: next.started_at ?? prev.started_at,
    duration_ms: next.duration_ms ?? prev.duration_ms,
    errors: next.errors?.length ? next.errors : prev.errors,
  };
}

/** Whether `value` is a status this build knows (guards a query-string filter). */
export function isClusterRunStatus(value: string): value is ClusterRunStatus {
  return (CLUSTER_RUN_STATUSES as readonly string[]).includes(value);
}
